import { Component, Input } from '@angular/core'
import { ProfileService } from '../../services/profile/profile.service'
import { Profile } from '../../models/users.interface'
import { catchError, map, Observable, switchMap, tap } from 'rxjs'
import { Pagination } from '../../models/pagination.interface'
import { LayoutService } from '../../services/layout/layout.service'
import { ToastrService } from 'ngx-toastr'
import { ActivatedRoute, Router } from '@angular/router'
import { Filter, QueryGet } from '../../shared/query-types'
import { PAGINATION } from '../../shared/constants'
import { SearchComponent } from '../../components/search/search.component'
import { MatIcon } from '@angular/material/icon'
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
} from '@angular/material/table'
import { NgxPaginationModule } from 'ngx-pagination'
import { AsyncPipe } from '@angular/common'
import { MatDialog } from '@angular/material/dialog'
import { EditPasswordComponent } from '../edit-password/edit-password.component'
import { ProfileFormComponent } from '../profile-form/profile-form.component'
import { LoadingService } from '../../services/loadingService/loading.service'
import { PermisionsComponent } from '../permision/permisions/permisions.component'
import { GroupSelectComponent } from '../group/group-select/group-select.component'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { OnlysuperuserDirective } from '../../directives/onlysuperuser.directive'
import { Title } from '@angular/platform-browser'

@Component({
    selector: 'app-profile-list',
    standalone: true,
    imports: [
        MatIcon,
        MatTable,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatCellDef,
        MatCell,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        NgxPaginationModule,
        AsyncPipe,
        SearchComponent,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        OnlysuperuserDirective,
    ],
    templateUrl: './profile-list.component.html',
    styleUrl: './profile-list.component.css',
})
export class ProfileListComponent {
    public page: number | undefined
    @Input() filtro: string = ''
    @Input() orden: string = ''
    limit = 10
    total = 0
    profileList: Profile[] = []
    asyncProfile!: Observable<Pagination<Profile[]>>
    selectedProfile: Profile | null = null
    showColumns: string[] = [
        'acciones',
        'id',
        'usuario',
        'nombres',
        'email',
        'fechaRegistro',
        'permisos',
        'grupos',
    ]

    constructor(
        private profileService: ProfileService,
        private layoutService: LayoutService,
        private toastr: ToastrService,
        private activatedRouter: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
        private loadingService: LoadingService,
        private title: Title
    ) {this.title.setTitle('Perfil')}

    ngOnInit(): void {
        this.layoutService.paginationLimit$
            .pipe(
                switchMap((limit) => {
                    this.limit = limit
                    return this.activatedRouter.queryParamMap
                }),
                switchMap((params) => {
                    const pageNum = params.get('page') || '1'
                    const search = params.get('search') || ''
                    this.page = parseInt(pageNum)
                    this.fetchProfile(PAGINATION.first_page, null, search)
                    return this.layoutService.paginationLimit$
                })
            )
            .subscribe()
    }

    fetchProfile(
        page: number,
        filter: Filter | null = null,
        search = ''
    ): void {
        const offset = page > 1 ? page * this.limit - this.limit : 0
        const params: QueryGet = {
            order: '-id',
            limit: this.limit,
            offset,
            search,
            filters: [{ name: 'is_active', value: 1 }],
        }
        if (filter) {
            params.filters.push(filter)
        }
        this.page = page
        this.loadingService.show(true)
        this.asyncProfile = this.profileService.allProfile(params).pipe(
            tap((response) => {
                this.profileList = response.results
                this.profileService.setProfiles(response.results)
                this.total = response.count
            }),
            map((res) => {
                this.loadingService.show(false)
                return res
            }),
            catchError((error) => {
                this.loadingService.show(false)
                this.router.navigate(['home/'])
                return []
            })
        )
    }

    updatePassword(profile: Profile) {
        this.selectedProfile = profile
        const dialogRef = this.dialog.open(EditPasswordComponent, {
            data: { selectedProfile: this.selectedProfile },
            width: '90vw',
            maxWidth: '80vw',
            maxHeight: '80vh',
        })
        dialogRef.afterClosed().subscribe(() => {
            this.fetchProfile(PAGINATION.first_page)
        })
    }

    createProfile() {
        this.profileService.removeProfile()
        const dialogRef = this.dialog.open(ProfileFormComponent, {
            width: '50%',
            height: 'auto',
            maxHeight: '80vh',
        })
        dialogRef.afterClosed().subscribe(() => {
            this.fetchProfile(PAGINATION.first_page)
        })
    }

    editar(profile: Profile) {
        this.profileService.setProfile(profile)
        const dialogRef = this.dialog.open(ProfileFormComponent, {
            width: 'auto',
            height: 'auto',
            maxHeight: '80vh',
        })
        dialogRef.afterClosed().subscribe(() => {
            this.fetchProfile(PAGINATION.first_page)
        })
    }

    openDialog(profile: Profile, action: 'group' | 'user' = 'group') {
        if (action === 'group') {
            const dialogRef = this.dialog.open(GroupSelectComponent, {
                width: 'auto',
                height: 'auto',
                maxHeight: '80vh',
                data: { profile },
            })
            dialogRef.afterClosed().subscribe(() => {
                this.fetchProfile(PAGINATION.first_page)
            })
        }
        if (action === 'user') {
            const dialogRef = this.dialog.open(PermisionsComponent, {
                width: 'auto',
                height: 'auto',
                maxHeight: '80vh',
                data: { profile },
            })
            dialogRef.afterClosed().subscribe(() => {
                this.fetchProfile(PAGINATION.first_page)
            })
        }
    }

    onRevokePermisions(profile: Profile) {
        const confirmMsg = `Esta operación revocará todos los accesos del usuario: ${profile.username}-${profile.first_name} ${profile.last_name}\n\n¿Desea continuar?`
        const isConfirmed = confirm(confirmMsg)

        if (!isConfirmed) return
        this.profileService.revokePermissions(profile.id).subscribe({
            next: () => {
                this.toastr.success(
                    `Se revocaron todos los permisos del usuario ${profile.username}-${profile.first_name} ${profile.last_name}, correctamente!`,
                    'Exitoso!'
                )
                this.fetchProfile(PAGINATION.first_page)
            },
            error: (err) => {
                this.toastr.error(
                    `No pudimos revocar los permisos del usuario ${profile.username}-${profile.first_name} ${profile.last_name}`,
                    'Error'
                )
            },
        })
    }

    onRevokeGroups(profile: Profile) {
        const confirmMsg = `Esta operacion revocará todos los grupos al usuario ${profile.username} ¿Estas seguro?`
        const isConfirmed = confirm(confirmMsg)

        if (!isConfirmed) return
        this.profileService.revokeGroups(profile.id).subscribe({
            next: () => {
                this.toastr.success(
                    `Se revocaron todos los grupos del usuario ${profile.username}-${profile.first_name} ${profile.last_name}, correctamente!`,
                    'Exitoso!'
                )
                this.fetchProfile(PAGINATION.first_page)
            },
            error: (err) => {
                this.toastr.error(
                    `No se pudimos revocar los grupos del usuario ${profile.username}-${profile.first_name} ${profile.last_name}`,
                    'Error'
                )
            },
        })
    }

    onDelete(id: number) {
        const confirmMsg = `¿Está seguro de eliminar este usuario?`
        const isConfirmed = confirm(confirmMsg)

        if (!isConfirmed) return
        this.profileService.deleteProfile(id).subscribe({
            next: () => {
                this.toastr.success(
                    `Usuario eliminado correctamente!`,
                    'Exitoso!'
                )
                this.fetchProfile(PAGINATION.first_page)
            },
            error: (err) => {
                this.toastr.error(`No se pudimos eliminar el usuario`, 'Error')
            },
        })
    }
}
